import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEstados } from '../../hooks/useEstados';
import { useUsuarioStore } from '../../store/usuarioStore';
import { CustomCheckbox } from '../CustomCheckbox/CustomCheckbox';
import { getStatusColor, getMPStatusColor } from '../../utils/statusUtils';
import { type ListarLicitacionesUi } from '../../schemas/licitaciones/listar-licitaciones/ui.schema';
import { type ListarComprasAgilesUi } from '../../schemas/compras-agiles/listar-compras-agiles/ui.schema';
import { LicitacionesService } from '../../services/licitaciones.service';
import { ComprasAgilesService } from '../../services/compras-agiles.service';
import { useLicitacionesStore } from '../../store/licitacionesStore';
import { useFiltersStore } from '../../store/filtersStore';

type ItemData = ListarLicitacionesUi | ListarComprasAgilesUi;

interface LicitacionesListProps {
  data: ItemData[];
  emptyMessage?: string;
  detailBasePath?: string;
  onRefresh?: () => void;
  onPageChange?: (page: number, limit: number) => void;
  entityType?: 'licitaciones' | 'compras-agiles';
  page?: number;
  limit?: number;
  total?: number;
}

const LicitacionesList = ({
  data,
  emptyMessage = "No se encontraron licitaciones",
  detailBasePath = "/licitacion",
  onRefresh,
  onPageChange,
  entityType = 'licitaciones',
  page: externalPage = 1,
  limit: externalLimit = 20,
  total: externalTotal = 0
}: LicitacionesListProps) => {
  const [page, setPage] = useState(externalPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(externalLimit);
  const navigate = useNavigate();
  const { estadosMPInverso } = useEstados();
  const { usuario } = useUsuarioStore();
  const { marcarComoVistoLocal } = useLicitacionesStore();
  const { activeStatusFilters, searchTerm, postuladoFilter, adjudicadoFilter, noLeidasFilter } = useFiltersStore();
  const recentlyViewedRef = useRef<Set<number>>(new Set());

  const isLicitacionVista = useCallback((licitacion: ItemData): boolean => {
    const userId = usuario?.id;
    if (!userId) return false;

    if (entityType === 'compras-agiles') {
      const compraAgilConVisto = licitacion as ListarComprasAgilesUi;
      return !!(compraAgilConVisto.visto && compraAgilConVisto.visto.includes(userId));
    }
    const licitacionConVisto = licitacion as ListarLicitacionesUi;
    return !!(licitacionConVisto.visto && licitacionConVisto.visto.includes(userId));
  }, [usuario?.id, entityType]);

  useEffect(() => {
    recentlyViewedRef.current.clear();
  }, [activeStatusFilters, searchTerm, postuladoFilter, adjudicadoFilter, noLeidasFilter, externalPage, externalTotal]);

  useEffect(() => {
    setPage(externalPage - 1);
    setRowsPerPage(externalLimit);
  }, [externalPage, externalLimit]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    onPageChange?.(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    onPageChange?.(1, newRowsPerPage);
  };

  const handleRowClick = (licitacion: ItemData) => {
    if (!isLicitacionVista(licitacion) && usuario?.id) {
      recentlyViewedRef.current.add(licitacion.id);

      if (entityType === 'licitaciones') {
        marcarComoVistoLocal(licitacion.id, usuario.id);
      }

      const service = entityType === 'compras-agiles'
        ? ComprasAgilesService.marcarComoVisto(licitacion.id)
        : LicitacionesService.marcarComoVisto(licitacion.id);

      service.catch((error) => {
        console.error(`Error al marcar ${entityType === 'compras-agiles' ? 'compra ágil' : 'licitación'} como vista:`, error);
      });
    }

    navigate(`${detailBasePath}/${licitacion.codigo_externo}`);
  };

  const getItemId = (licitacion: ItemData) => licitacion.codigo_externo;

  const totalForPagination = externalTotal > 0 ? externalTotal : data.length;
  const safePage = totalForPagination === 0 ? 0 : Math.min(page, Math.max(0, Math.ceil(totalForPagination / rowsPerPage) - 1));

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabla de licitaciones">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" width="5%">
                <CustomCheckbox
                  items={data}
                  getItemId={getItemId}
                />
              </TableCell>
              <TableCell width="3%" align="center">Estado</TableCell>
              <TableCell width="10%" align="center">Postulado / Adjudicado</TableCell>
              <TableCell width="10%" align="center">Fecha de publicación</TableCell>
              <TableCell width="20%" align="center" sx={{ maxWidth: 0 }}>{entityType === "licitaciones" ? "Nombre de licitación" : "Nombre de compra ágil"}</TableCell>
              <TableCell width="30%" align="center" sx={{ maxWidth: 0 }}>Resumen</TableCell>
              <TableCell width="10%" align="center">Fecha de Cierre</TableCell>
              <TableCell width="17%" align="center">Estado MP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((licitacion) => (
                <TableRow
                  key={licitacion.id}
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  onClick={() => handleRowClick(licitacion)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <CustomCheckbox
                      item={licitacion}
                      items={data}
                      getItemId={getItemId}
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(Number(licitacion.estado_licitacion)),
                        margin: '0 auto'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '12px' }}>
                     {licitacion.adjudicado
                      ? <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Adjudicado</span>
                      : licitacion.postulado
                        ? <span style={{ color: '#2196f3', fontWeight: 'bold' }}>Postulado</span>
                        : ""}
                   </TableCell>
                  <TableCell align="center" sx={{ fontSize: '12px' }}>
                    {licitacion.fecha_creacion
                      ? new Date(licitacion.fecha_creacion).toLocaleDateString()
                      : '-'
                    }
                  </TableCell>
                  <Tooltip
                    title={licitacion.nombre}
                    enterDelay={300}
                    placement="top-start"
                    disableHoverListener={false}
                    slotProps={{
                      tooltip: {
                        sx: { maxWidth: 400 },
                      },
                    }}
                  >
                    <TableCell sx={{
                      maxWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#0095FF',
                      fontSize: '12px',
                      fontWeight: !isLicitacionVista(licitacion) ? 'bold' : 'normal'
                    }}>
                      {licitacion.nombre}
                    </TableCell>
                  </Tooltip>
                  <Tooltip
                    title={licitacion.resumen || ''}
                    enterDelay={300}
                    placement="top-start"
                    slotProps={{
                      tooltip: { sx: { maxWidth: 400 } },
                    }}
                  >
                    <TableCell sx={{
                      maxWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '12px',
                      fontWeight: !isLicitacionVista(licitacion) ? 'bold' : 'normal'
                    }}>
                      {licitacion.resumen}
                    </TableCell>
                  </Tooltip>
                  <TableCell align="center" sx={{ fontSize: '12px' }}>
                    {licitacion.fecha_cierre
                      ? new Date(licitacion.fecha_cierre).toLocaleDateString()
                      : '-'
                    }
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: Number(licitacion.estado_licitacion) === 4
                        ? '#9e9e9e'
                        : getMPStatusColor(estadosMPInverso[Number(licitacion.codigo_estado)] || ''),
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}
                  >
                    {Number(licitacion.estado_licitacion) === 4
                      ? 'Sin seguimiento'
                      : (estadosMPInverso[Number(licitacion.codigo_estado)] || '')
                    }
                  </TableCell>
                </TableRow>
              ))}
            {data.length === 0 && (
              <TableRow sx={{
                height: '28px',
                '& td': {
                  borderWidth: '1px'
                }
              }}>
                <TableCell colSpan={7} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 20, 40]}
        component="div"
        count={totalForPagination}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página"
      />
    </Box>
  );
};

export { LicitacionesList };
export default LicitacionesList;
